import type { DeploymentStatusDTO, StatsDTO } from "@shared/types";
import { formatBytes } from "../lib/format";
import { Button } from "./Button";

interface Props {
  stats: StatsDTO | null;
  deployment: DeploymentStatusDTO | null;
  publishing: boolean;
  onPublishNow: () => void;
}

/**
 * Library totals. Deployment plumbing is deliberately invisible here: with the
 * local driver an upload is readable immediately, so a progress line would be
 * noise. Only a genuine publish failure surfaces, because that needs action.
 */
export function StatsBar({ stats, deployment, publishing, onPublishNow }: Props) {
  const failed = deployment?.state === "error";

  return (
    <div className="space-y-2">
      <div className="glass grid grid-cols-3 gap-x-3 gap-y-3 rounded-xl px-4 py-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-8 sm:px-5 sm:py-3.5">
        <Stat label="Images" value={(stats?.images ?? 0).toLocaleString()} />
        <Divider />
        <Stat label="Folders" value={(stats?.folders ?? 0).toLocaleString()} />
        <Divider />
        <Stat label="Total size" value={formatBytes(stats?.totalSizeBytes ?? 0)} />
      </div>

      {failed && (
        <div className="glass flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border-red-200/80 px-4 py-2.5">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
          <p className="min-w-0 flex-1 break-words text-[13px] text-red-700">
            {deployment?.lastError ?? "The last publish did not finish."}
          </p>
          <Button size="sm" variant="danger" loading={publishing} onClick={onPublishNow}>
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 leading-none">
      <span className="truncate text-2xs uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="truncate text-sm font-semibold tabular-nums text-zinc-900 sm:text-[15px]">{value}</span>
    </div>
  );
}

function Divider() {
  return <span className="hidden h-8 w-px bg-zinc-900/10 sm:block" />;
}
